import React, { useState, useEffect, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StudentSelectionGrid } from './StudentSelectionGrid';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { toast } from 'sonner';
import { useMessagesTranslations } from './hooks/useMessagesTranslations';
import { Channel } from './types';

type EditChannelDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: Channel | null;
  onChannelUpdated: (channel: Channel) => void;
};

type Student = {
  auth_id: string;
  first_name: string;
  last_name: string;
  avatar_url?: string | null;
};

export const EditChannelDialog = ({ 
  open, 
  onOpenChange, 
  channel, 
  onChannelUpdated 
}: EditChannelDialogProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { t } = useMessagesTranslations();

  useEffect(() => {
    if (!user || !open || !channel) return;

    const fetchData = async () => {
      try {
        // Fetch channel members
        const { data: members, error: membersError } = await supabase
          .from('channel_members')
          .select('user_id')
          .eq('channel_id', channel.id)
          .neq('role', 'owner');

        if (membersError) throw membersError;

        // Fetch students
        const { data: studentsData, error: studentsError } = await supabase
          .from('students')
          .select('auth_id, first_name, last_name, avatar_url')
          .eq('trainer_id', user.id)
          .eq('status', 'active');

        if (studentsError) throw studentsError;

        setStudents(studentsData || []);
        setSelectedStudents(members?.map(m => m.user_id) || []);
        setName(channel.name);
        setDescription(channel.description || '');
      } catch (error) {
        console.error('Error fetching channel data:', error);
        toast.error(t('errorLoading'));
      }
    };

    fetchData();
  }, [user, channel, open]);

  // Memoize handlers to stabilize their identity
  const handleSelectStudent = useCallback((studentId: string) => {
    console.log("🚀 [EditChannelDialog] handleSelectStudent called with:", studentId);
    setSelectedStudents(prev => {
      // Ensure no duplicates are added (important for stability)
      if (prev.includes(studentId)) return prev;
      return [...prev, studentId];
    });
  }, []); // Empty dependency array means this function's identity is stable

  const handleRemoveStudent = useCallback((studentId: string) => {
    console.log("🚀 [EditChannelDialog] handleRemoveStudent called with:", studentId);
    setSelectedStudents(prev => prev.filter(id => id !== studentId));
  }, []); // Empty dependency array means this function's identity is stable

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !channel) return;
    
    if (!name.trim()) {
      toast.error(t('channelNameRequired'));
      return;
    }
    
    try {
      setLoading(true);
      console.log("🚀 [EditChannelDialog] handleSubmit: Updating channel...");
      
      // Update channel
      const { data: channelData, error: channelError } = await supabase
        .from('message_channels')
        .update({
          name: name.trim(),
          description: description.trim() || null,
        })
        .eq('id', channel.id)
        .select()
        .single();
      
      if (channelError) {
        console.error("🚨 Update channel error:", channelError);
        throw channelError;
      }
      console.log("✅ [EditChannelDialog] handleSubmit: Channel updated successfully:", channelData);

      console.log("🚀 [EditChannelDialog] handleSubmit: Deleting existing members (excluding owner)...");
      // Remove existing members except owner
      const { error: deleteError } = await supabase // Capture delete error separately
        .from('channel_members')
        .delete()
        .eq('channel_id', channel.id)
        .neq('role', 'owner');

      if (deleteError) {
        console.error("🚨 Delete members error:", deleteError);
        throw deleteError;
      }
      console.log("✅ [EditChannelDialog] handleSubmit: Existing members deleted successfully.");

      // Add updated members list
      if (selectedStudents.length > 0) {
        console.log("🚀 [EditChannelDialog] handleSubmit: Preparing new members from:", selectedStudents);
        
        // Filter out any null/undefined values before mapping
        const validStudentIds = selectedStudents.filter(id => id);
        
        if (validStudentIds.length > 0) {
          console.log("🚀 [EditChannelDialog] handleSubmit: Inserting valid members:", validStudentIds);
          const members = validStudentIds.map(studentId => ({
            channel_id: channel.id,
            user_id: studentId, // Now guaranteed to be non-null
            role: 'member'
          }));

          const { error: membersError } = await supabase
            .from('channel_members')
            .insert(members);
          
          if (membersError) {
            console.error("🚨 Insert members error:", membersError);
            throw membersError;
          }
          console.log("✅ [EditChannelDialog] handleSubmit: New members inserted successfully.");
        } else {
          console.log("ℹ️ [EditChannelDialog] handleSubmit: No valid members to insert after filtering.");
        }
      } else {
        console.log("ℹ️ [EditChannelDialog] handleSubmit: No selected students to insert.");
      }
      
      console.log("🚀 [EditChannelDialog] handleSubmit: Calling callbacks and closing...");
      toast.success(t('channelUpdated'));
      onChannelUpdated(channelData);
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating channel:', error);
      toast.error(t('errorUpdatingChannel'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('editChannel')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="name">{t('channelName')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('channelNamePlaceholder')}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="description">{t('channelDescription')}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('channelDescriptionPlaceholder')}
              rows={3}
            />
          </div>
          <DialogFooter className="mt-4 pt-2 border-t">
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={loading || !name.trim()}>
              {loading ? t('updating') : t('update')}
            </Button>
          </DialogFooter>
        </form>

        <div className="grid gap-2 mt-4 pt-4 border-t dark:border-gray-700">
          <Label>{t('participants')}</Label>
          <StudentSelectionGrid
            students={students}
            selectedStudents={selectedStudents}
            onSelectStudent={handleSelectStudent}
            onRemoveStudent={handleRemoveStudent}
          />
        </div>

      </DialogContent>
    </Dialog>
  );
};
